// Dialog s detailem polozky operativy.
//
// - Naciatame cerstvy detail polozky z `/polozky/{id}` (zaruci, ze vidime
//   nejnovejsi material/report a ne jen kopii z list endpointu).
// - Form pro zapis reportu se obvykle vola PUT /polozky/{id}/report — backend
//   bere optional pole, takze posilame jen to, co ma user vyplnit.
// - Editovatelnost je rizena rolemi (viz `useReportPermissions`) a stavem
//   operativy (SCHVALENO / V_REALIZACI / UPRAVENA / UZAVRENO — viz STAVY_S_REPORTEM).
// - Material je dynamicky list radku {co, kolik} s napovedou pres
//   MaterialAutocomplete.

import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { STAVY_S_REPORTEM, usePermissions } from '@/lib/permissions';
import { formatDateTimeCs } from '@/lib/utils';
import type {
  MaterialInput,
  Polozka,
  PolozkaReportInput,
  StavOperativy,
  StavPolozky,
} from '@/types/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RichText, RichTextView, richKUlozeni } from '@/components/ui/rich-text';
import { useToast } from '@/components/ui/use-toast';
import { StavBadge } from '@/components/StavBadge';
import { MaterialAutocomplete } from '@/components/MaterialAutocomplete';

interface Props {
  open: boolean;
  polozkaId: string | null;
  operativaStav: StavOperativy;
  onClose: () => void;
  // Klic operativy pro invalidaci po uspechu (aby se prekreslila tabulka).
  invalidateOnSuccess?: ReadonlyArray<unknown>;
}

const STAVY_POLOZKY: { value: StavPolozky; label: string }[] = [
  { value: 'NAPLANOVANO', label: 'Naplánováno' },
  { value: 'PROVEDENO', label: 'Provedeno' },
  { value: 'PROVEDENO_CASTECNE', label: 'Provedeno částečně' },
  { value: 'NEPROVEDENO', label: 'Neprovedeno' },
];

export function PolozkaDetailDialog({
  open,
  polozkaId,
  operativaStav,
  onClose,
  invalidateOnSuccess,
}: Props) {
  const allPerms = usePermissions();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  // Adapter z centralizovanych perms na lokalni nazvy (drzime existujici JSX bez zmen).
  // `muzeEditovatPole` ignoruje stav operativy (jen role) — to potrebujeme pro renderovani sekci.
  // Stav-aware gating (disabled inputu) resime pres `stavUmoznujeReport`.
  const perms = {
    muzeVykonUkony: allPerms.muzeEditovatPole('vykon'),
    muzeMaterial: allPerms.muzeEditovatPole('material'),
    muzeZjisteni: allPerms.muzeEditovatPole('zjisteni'),
    muzeKontrola: allPerms.muzeEditovatPole('kontrola'),
    muzeStav: allPerms.muzeEditovatPole('stav'),
    get muzeNeco() {
      return (
        this.muzeVykonUkony
        || this.muzeMaterial
        || this.muzeZjisteni
        || this.muzeKontrola
      );
    },
  };
  const stavUmoznujeReport = STAVY_S_REPORTEM.includes(operativaStav);

  const polozkaQuery = useQuery<Polozka>({
    queryKey: ['polozka', polozkaId],
    queryFn: () => api<Polozka>(`/polozky/${polozkaId}`),
    enabled: open && polozkaId !== null,
  });

  const polozka = polozkaQuery.data;

  // Formularni state — pohlcuji se z polozky pri kazde zmene polozkaId / openu.
  const [osoba, setOsoba] = useState('');
  const [stav, setStav] = useState<StavPolozky>('NAPLANOVANO');
  const [vykon, setVykon] = useState('');
  const [ukony, setUkony] = useState('');
  const [zjisteni, setZjisteni] = useState('');
  const [kontrola, setKontrola] = useState('');
  const [material, setMaterial] = useState<MaterialInput[]>([]);

  useEffect(() => {
    if (!open || !polozka) return;
    setOsoba(polozka.osoba ?? '');
    setStav(polozka.stav);
    setVykon(polozka.vykonPrace ?? '');
    setUkony(polozka.provedeneUkony ?? '');
    setZjisteni(polozka.zjisteni ?? '');
    // kontrola_text je "audit log" — pri kazdem ulozeni se zapise novy text.
    // Defaultne necham prazdny, aby user nemusel mazat predchozi text.
    setKontrola('');
    setMaterial(polozka.material.map((m) => ({ co: m.co, kolik: m.kolik })));
  }, [open, polozka]);

  const reportMutation = useMutation({
    mutationFn: (input: PolozkaReportInput) =>
      api<Polozka>(`/polozky/${polozkaId}/report`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['polozka', polozkaId] });
      if (invalidateOnSuccess) {
        queryClient.invalidateQueries({ queryKey: invalidateOnSuccess });
      }
      toast({ title: 'Report uložen' });
      onClose();
    },
    onError: (err: Error) =>
      toast({ title: 'Uložení selhalo', description: err.message, variant: 'destructive' }),
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!polozka) return;

    // Posilame jen pole, ke kterym user ma prava. Backend dela merge a nepresape
    // ostatni hodnoty.
    const input: PolozkaReportInput = {};
    if (perms.muzeStav) input.stav = stav;
    if (perms.muzeVykonUkony) {
      input.vykon = richKUlozeni(vykon);
      input.ukony = richKUlozeni(ukony);
    }
    if (perms.muzeMaterial) {
      input.material = material
        .map((m) => ({ co: m.co.trim(), kolik: m.kolik?.trim() ?? '' }))
        .filter((m) => m.co !== '')
        .map((m) => ({ co: m.co, kolik: m.kolik === '' ? null : m.kolik }));
    }
    if (perms.muzeZjisteni) {
      input.zjisteni = richKUlozeni(zjisteni);
    }
    if (perms.muzeKontrola) {
      // Kontrola se zapise jen kdyz user neco vyplnil — historie se zachovava.
      const txt = richKUlozeni(kontrola);
      if (txt !== null) input.kontrola = txt;
    }

    reportMutation.mutate(input);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {polozka
              ? `Položka #${polozka.poradi} — ${polozka.mechanizaceKod}`
              : 'Položka operativy'}
          </DialogTitle>
        </DialogHeader>

        {polozkaQuery.isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : polozkaQuery.isError ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
            Načtení selhalo: {(polozkaQuery.error as Error).message}
          </div>
        ) : polozka ? (
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Plan (read-only) */}
            <div className="space-y-3 rounded-md border bg-muted/30 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs text-muted-foreground">Mechanizace</div>
                  <div className="font-medium">
                    {polozka.mechanizaceKod} — {polozka.mechanizaceNazev}
                  </div>
                </div>
                <StavBadge stav={polozka.stav} type="polozka" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Popis činnosti</div>
                <RichTextView
                  value={polozka.popisCinnosti}
                  className="max-h-48 overflow-y-auto leading-relaxed"
                />
              </div>
              {polozka.kontrolaText && (
                <div>
                  <div className="text-xs text-muted-foreground">
                    Poslední kontrola (
                    {polozka.kontrolovalJmeno ?? '—'}
                    {polozka.kontrolovano ? `, ${formatDateTimeCs(polozka.kontrolovano)}` : ''}
                    )
                  </div>
                  <RichTextView value={polozka.kontrolaText} />
                </div>
              )}
            </div>

            {!stavUmoznujeReport && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                Operativa je ve stavu <strong>{operativaStav}</strong>. Report bude možný po
                schválení (stavy Schváleno, V realizaci, Upravená nebo Uzavřeno).
              </div>
            )}

            {/* Zamestnanci (jen informativni v dialogu) */}
            <div className="space-y-1.5">
              <Label htmlFor="osoba">Zaměstnanci</Label>
              <Input
                id="osoba"
                value={osoba}
                disabled
                readOnly
                title="Zaměstnanci se mění přes úpravu položky v operativě."
              />
              <p className="text-xs text-muted-foreground">
                Zaměstnance lze změnit přes úpravu položky v operativě.
              </p>
            </div>

            {/* Stav polozky */}
            {perms.muzeStav && (
              <div className="space-y-1.5">
                <Label htmlFor="stav-polozky">Stav položky</Label>
                <Select
                  value={stav}
                  onValueChange={(v) => setStav(v as StavPolozky)}
                  disabled={!stavUmoznujeReport}
                >
                  <SelectTrigger id="stav-polozky">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STAVY_POLOZKY.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Vykon prace + Ukony — VEDOUCI / ADMIN */}
            {perms.muzeVykonUkony && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="vykon">Výkon práce</Label>
                  <RichText
                    id="vykon"
                    value={vykon}
                    onChange={setVykon}
                    disabled={!stavUmoznujeReport}
                    minHeightClass="min-h-[4rem]"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ukony">Provedené úkony</Label>
                  <RichText
                    id="ukony"
                    value={ukony}
                    onChange={setUkony}
                    disabled={!stavUmoznujeReport}
                    minHeightClass="min-h-[5rem]"
                  />
                </div>
              </>
            )}

            {/* Material */}
            {perms.muzeMaterial && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Spotřebovaný materiál</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!stavUmoznujeReport}
                    onClick={() => setMaterial((m) => [...m, { co: '', kolik: null }])}
                  >
                    <Plus className="mr-1 h-3 w-3" /> Přidat
                  </Button>
                </div>
                {material.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Žádný materiál. Pro přidání klikněte na „Přidat".
                  </p>
                ) : (
                  <div className="space-y-2">
                    {material.map((row, idx) => (
                      <div key={idx} className="flex gap-2">
                        <div className="flex-1">
                          <MaterialAutocomplete
                            value={row.co}
                            onChange={(v) =>
                              setMaterial((m) =>
                                m.map((it, i) => (i === idx ? { ...it, co: v } : it)),
                              )
                            }
                            placeholder="Co (např. trubka 50mm)"
                            disabled={!stavUmoznujeReport}
                          />
                        </div>
                        <Input
                          className="w-40"
                          placeholder="Kolik (5 m, 2 ks…)"
                          value={row.kolik ?? ''}
                          onChange={(e) =>
                            setMaterial((m) =>
                              m.map((it, i) =>
                                i === idx ? { ...it, kolik: e.target.value } : it,
                              ),
                            )
                          }
                          disabled={!stavUmoznujeReport}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={!stavUmoznujeReport}
                          onClick={() =>
                            setMaterial((m) => m.filter((_, i) => i !== idx))
                          }
                          title="Odebrat řádek"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Zjisteni */}
            {perms.muzeZjisteni && (
              <div className="space-y-1.5">
                <Label htmlFor="zjisteni">Zjištění</Label>
                <RichText
                  id="zjisteni"
                  value={zjisteni}
                  onChange={setZjisteni}
                  disabled={!stavUmoznujeReport}
                  minHeightClass="min-h-[5rem]"
                />
              </div>
            )}

            {/* Kontrola — pouze TECHNIK_INT / SCHVALOVATEL / ADMIN */}
            {perms.muzeKontrola && (
              <div className="space-y-1.5">
                <Label htmlFor="kontrola">Nový text kontroly</Label>
                <RichText
                  id="kontrola"
                  value={kontrola}
                  onChange={setKontrola}
                  disabled={!stavUmoznujeReport}
                  placeholder="Pouze pokud chcete přidat / přepsat poznámku kontroly"
                  minHeightClass="min-h-[5rem]"
                />
                <p className="text-xs text-muted-foreground">
                  Prázdné pole nemaže předchozí kontrolu. Vyplňte jen pokud chcete zapsat novou.
                </p>
              </div>
            )}

            {!perms.muzeNeco && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                Vaše role neumožňuje zapisovat report k této položce.
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Zavřít
              </Button>
              {perms.muzeNeco && (
                <Button
                  type="submit"
                  disabled={!stavUmoznujeReport || reportMutation.isPending}
                >
                  {reportMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Uložit report'
                  )}
                </Button>
              )}
            </DialogFooter>
          </form>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
